import React, { useMemo, useState } from "react";
import styles from "./TopBar.module.scss";
import colors from "../../styles/variables.module.scss";

// icons
import LocationSelect from "./components/locationSelect/LocationSelect";
import CalendarDropdown, {
  selectedDatesI,
} from "./components/calendarDropdown/CalendarDropdown";
import TravelersDropdown from "./components/travelersDropdown/TravelersDropdown";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHooks";
import {
  selectColor,
  selectCouponCode,
} from "../../redux/slices/enviromentSlice/EnviromentSlice.selectors";
import UpdateUrlParams from "../../hooks/UpdateUrlParams";
import { useLanguage } from "../../hooks/languageContextHooks";
import styled from "styled-components";
import tinycolor from "tinycolor2";
import { zoneI } from "../../hooks/UbicationFilterContext";
import CustomButton from "../customButton/CustomButton";
import DropdownContainer from "../dropdownContainer/DropdownContainer";
import { useTranslation } from "react-i18next";
import { useHotelDataContext } from "../../hooks/HotelDataContextHook";
import { HotelDataListI } from "../../redux/slices/enviromentSlice/EnviromentSlice.type";
import {
  Box,
  InputAdornment,
  TextField,
  TextFieldProps,
  Typography,
} from "@mui/material";
import { AiOutlineCheck } from "@react-icons/all-files/ai/AiOutlineCheck";
import { setCouponCode } from "../../redux/slices/enviromentSlice/EnviromentSlice";
import { useWidgetConfigContext } from "src/contexts/widgetConfig/WidgetConfig.context";

interface propsI {
  onChangeUbication?: (ubication: zoneI) => void;
  onChangeDate?: (selectedDates: selectedDatesI) => void;
  onChangeChildren?: (childrenAges: number[]) => void;
  adults: number;
  childrenAges?: number[];
  onChangeAdults?: (adults: number) => void;
  onSearch?: () => void;
  dates: selectedDatesI;
  ubication?: zoneI;
  showLocation?: boolean;
}

const RoundedInput = ({
  InputProps: { sx, ...InputProps } = {},
  ...props
}: TextFieldProps) => (
  <TextField
    fullWidth
    InputProps={{
      sx: {
        borderRadius: 3,
        ...sx,
      },
      ...InputProps,
    }}
    {...props}
  />
);

const InputContainer = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      "&&&": {
        width: { xs: "100%", md: "20%" },
        marginTop: { xs: "1rem", md: 0 },
        marginLeft: { md: "1rem" },
      },
    }}
  >
    {children}
  </Box>
);

const TopBar: React.FC<propsI> = ({
  onChangeUbication,
  onChangeDate,
  onChangeChildren,
  adults,
  childrenAges,
  onChangeAdults,
  onSearch,
  dates,
  ubication,
  showLocation = true,
}) => {
  const mainColor = useAppSelector(selectColor);
  const couponCode = useAppSelector(selectCouponCode);
  const dispatch = useAppDispatch();

  const { hideLanguageSelector } = useWidgetConfigContext();

  const { updateUrlParams } = UpdateUrlParams();
  const { language, enabledLanguages } = useLanguage();
  const { t } = useTranslation();

  const { filteredHotelDataList: hotelDataList, loading } =
    useHotelDataContext();
  const couponMessage =
    hotelDataList.find((hotel: HotelDataListI) => hotel.couponMessage)
      ?.couponMessage || "";

  const isValidCoupon = hotelDataList.some(
    (hotel: HotelDataListI) => hotel.couponCodeValid
  )
    ? true
    : !Boolean(couponMessage);

  const [openLangDropdown, setOpenLangDropdown] = useState(false);

  const handleSelectLanguage = (e: React.MouseEvent, language: string) => {
    setOpenLangDropdown(false);
    updateUrlParams({
      updateLanguage: language,
    });
  };

  const handleSearch = () => {
    onSearch?.();
  };

  return (
    <div className={`${styles.mainContainer} ${styles.specificityWrapper}`}>
      <div className={styles.topBar}>
        <div
          className={styles.languageContainer}
          style={{ color: mainColor || colors.primaryBlue }}
        >
          {!hideLanguageSelector && (
            <>
              <div
                onClick={() => setOpenLangDropdown(true)}
                className={styles.languageContainer_dropdown}
              >
                <div className={styles.webIconContainer}>
                  <img
                    src="https://booking.availroom.com/v2/assets/icons/web.svg"
                    alt=""
                  />
                </div>
                {language.toUpperCase()}
                <div className={styles.dropdownIconContainer}>
                  <img
                    src="https://booking.availroom.com/v2/assets/icons/dropdown.svg"
                    alt=""
                  />
                </div>
              </div>
              {openLangDropdown && (
                <DropdownContainer
                  className={styles.languageDropdown}
                  onClickOutside={() => setOpenLangDropdown(false)}
                >
                  <div className={styles.languageDropdown_container}>
                    <ul>
                      {Object.entries(enabledLanguages).map(([key, value]) => (
                        <li
                          data-language={value}
                          onClick={(e: React.MouseEvent) =>
                            handleSelectLanguage(e, value as string)
                          }
                          key={key}
                        >
                          {key}
                        </li>
                      ))}
                    </ul>
                  </div>
                </DropdownContainer>
              )}
            </>
          )}
        </div>
        <div className={styles.topBar_leftContainer}>
          {showLocation && (
            <LocationSelect
              ubication={ubication}
              onChangeUbication={onChangeUbication}
            />
          )}
          <CalendarDropdown dates={dates} onChangeDate={onChangeDate} />
          <TravelersDropdown
            adults={adults}
            startChildrenAges={childrenAges || []}
            onChangeChildren={onChangeChildren}
            onChangeAdults={onChangeAdults}
          />
        </div>
        <InputContainer>
          <RoundedInput
            color={
              couponMessage ? (isValidCoupon ? "success" : "error") : undefined
            }
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  {couponMessage ? (
                    isValidCoupon ? (
                      <AiOutlineCheck color="green" />
                    ) : (
                      <img
                        src="https://booking.availroom.com/v2/assets/icons/redCross.svg"
                        alt="icon"
                        width="14px"
                      />
                    )
                  ) : (
                    <img
                      src="https://booking.availroom.com/v2/assets/icons/percentage.svg"
                      alt="icon"
                      width="14px"
                    />
                  )}
                </InputAdornment>
              ),
            }}
            onChange={(e) => dispatch(setCouponCode(e.target.value))}
            value={couponCode}
            placeholder={t("labels.couponCode")}
          />
          {couponMessage ? (
            isValidCoupon ? (
              <Typography sx={{ fontSize: "0.825em", position: "absolute" }}>
                {t("property.couponCode.button.applied")}
              </Typography>
            ) : (
              <Typography sx={{ fontSize: "0.825em", position: "absolute" }}>
                {t("property.couponCode.button.invalid")}
              </Typography>
            )
          ) : null}
        </InputContainer>
        <div className={styles.topBar_middleContainer}>
          <div className={styles.buttonContainer} onClick={handleSearch}>
            <CustomButton className={"searchBtn"} disabled={loading} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default TopBar;
